import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import {
  Mail, Lock, Loader2, Sparkles, ArrowRight, Shield,
  MessageCircle, Layout, Cpu, Compass, TrendingUp,
  FileText, Calculator, Zap, Printer, Layers, Clock,
  BookOpen, Sun, Moon, Check, ChevronRight, Play, Star,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { DPLIcon } from '@/components/brand/DPLLogo';
import { logger } from '@/lib/utils/logger';
import { DEFAULT_INDUSTRY_SLUG, INDUSTRY_OPTIONS } from '@/data/industryOptions';
import { useSEO } from '@/hooks/useSEO';

const ease = [0.16, 1, 0.3, 1] as const;
const fadeUp = {
  initial: { opacity: 0, y: 50, filter: 'blur(10px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
  transition: { duration: 1.2, ease },
};
const stagger = { animate: { transition: { staggerChildren: 0.1 } } };

function ParticleField() {
  return null; // Removed in premium redesign
}

function Ticker() {
  return (
    <div className="w-full border-y border-zinc-200 dark:border-white/5 bg-zinc-50 dark:bg-zinc-900/20 py-4 select-none flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
      <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-[0.12em]">Built for modern estimation workflows</span>
      <div className="flex items-center gap-6 text-[10px] font-medium text-zinc-600 dark:text-zinc-400">
        <span className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500 dark:text-emerald-400" /> Accurate Estimation</span>
        <span className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500 dark:text-emerald-400" /> Live Catalogs</span>
        <span className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500 dark:text-emerald-400" /> AI-Assisted Quotes</span>
      </div>
    </div>
  );
}

function FeaturePill({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  return (
    <motion.div variants={fadeUp}
      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-zinc-200 dark:border-white/5 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-all duration-200 ease-out hover:-translate-y-[1px] cursor-default text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200">
      <Icon className="h-4 w-4" strokeWidth={2} />
      <span className="text-[11px] font-medium uppercase tracking-[0.12em]">{label}</span>
    </motion.div>
  );
}

function BentoCard({ icon: Icon, title, sub, desc, wide = false, delay = 0 }:
  { icon: React.ElementType; title: string; sub: string; desc: string; accent?: boolean; wide?: boolean; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ duration: 0.5, ease, delay }}
      className={cn(
        'relative border border-zinc-200 dark:border-white/5 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 p-8 flex flex-col gap-6 overflow-hidden transition-all duration-200 ease-out hover:-translate-y-[1px]',
        wide ? 'md:col-span-2 rounded-2xl' : 'rounded-2xl'
      )}>
      <div className="text-zinc-400 dark:text-zinc-500">
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight mb-2">{title}</h3>
        <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-[0.12em] mb-4">{sub}</p>
        <p className="text-zinc-500 dark:text-zinc-400 text-sm leading-relaxed">{desc}</p>
      </div>
    </motion.div>
  );
}

type Tab = 'login' | 'signup' | 'forgot';
type RegStep = 'plans' | 'identity' | 'industry' | 'form' | 'sent';

export default function Login() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  useSEO({
    title: 'DCode DEW | High-Precision Interior Estimation & Quotation Software',
    description: 'Professional interior design estimation and project financial management software. Create cinematic PDFs, automate GST, and track accounting.',
    keywords: 'Interior Design Estimating Software, Construction Quote Generator, Commercial Quotes, GST Automation, Proposal Generator, Billing Software',
  });

  const [tab, setTab] = useState<Tab>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [regStep, setRegStep] = useState<RegStep>('plans');
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'pro' | 'enterprise'>('starter');
  const [regForm, setRegForm] = useState({
    email: '', password: '', fullName: '', companyName: '', nickname: '',
    accountType: 'company' as 'company' | 'individual', industrySlug: DEFAULT_INDUSTRY_SLUG,
  });
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reset') === '1') { setTab('forgot'); window.history.replaceState({}, '', '/login'); return; }

    // Only auto-redirect if the user didn't explicitly navigate here.
    // If they clicked "Login" or typed /login, let them stay on the marketing page.
    // We use the 'stay' param or referrer heuristics to decide.
    // Skip auto-redirect entirely — the user explicitly visited /login.
    // They'll be redirected after successful login via handleLogin → navigate('/').
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast.success('Welcome back!', { description: 'Your workspace is loading.' });
      navigate('/');
    } catch (err: unknown) { toast.error('Login failed', { description: (err as Error).message }); }
    finally { setLoginLoading(false); }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault(); setResetLoading(true);
    try {
      const site = import.meta.env.VITE_SITE_URL || 'https://dew.dcodeinteriors.com';
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, { redirectTo: `${site}/login?reset=1` });
      if (error) throw error;
      setResetSent(true);
    } catch (err: unknown) { toast.error('Reset failed', { description: (err as Error).message }); }
    finally { setResetLoading(false); }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (regForm.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSigning(true);
    try {
      const site = import.meta.env.VITE_SITE_URL || 'https://dew.dcodeinteriors.com';
      const slug = regForm.nickname || regForm.companyName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 30) || 'workspace';
      const { data: authData, error } = await supabase.auth.signUp({
        email: regForm.email, password: regForm.password,
        options: { emailRedirectTo: `${site}/?setup=1`, data: {
          full_name: regForm.fullName, company_name: regForm.companyName, company_nickname: slug,
          plan: selectedPlan, account_type: regForm.accountType, industry_slug: regForm.industrySlug,
        }},
      });
      if (error) throw error;
      if (!authData.user) throw new Error('Signup failed — please try again.');
      localStorage.setItem('dew_pending_setup', JSON.stringify({
        companyName: regForm.companyName, nickname: slug, plan: selectedPlan,
        fullName: regForm.fullName, accountType: regForm.accountType, industrySlug: regForm.industrySlug,
      }));
      logger.log(`[Signup] Done — slug: ${slug}`);
      setRegStep('sent');
      toast.success('Check your inbox to confirm!');
    } catch (err: unknown) { toast.error('Registration failed', { description: (err as Error).message }); }
    finally { setSigning(false); }
  };

  const plans = [
    { id: 'starter' as const, name: 'Starter', desc: 'Solo · 1 industry', price: 'Free Forever', icon: Calculator, badge: null },
    { id: 'pro' as const, name: 'Pro', desc: 'Team of 10 · All industries', price: '₹2,499/mo', icon: Sparkles, badge: 'Popular' },
    { id: 'enterprise' as const, name: 'Elite', desc: 'Custom branding · API', price: 'Contact Us', icon: Shield, badge: null },
  ];

  const industries = INDUSTRY_OPTIONS;
  /*
    { slug: 'interior-design', label: 'Interior Design', icon: '🛋️' },
    { slug: 'civil-construction', label: 'Civil Construction', icon: '🏗️' },
    { slug: 'landscape', label: 'Landscape', icon: '🌿' },
    { slug: 'architecture', label: 'Architecture', icon: '📐' },
    { slug: 'photography', label: 'Photography', icon: '📸' },
    { slug: 'events', label: 'Events', icon: '🎪' },
    { slug: 'it-consulting', label: 'IT Consulting', icon: '💻' },
    { slug: 'furniture', label: 'Furniture', icon: '🪑' },
  */

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 60]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const inputCls = "h-12 bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-white/10 text-zinc-900 dark:text-zinc-100 rounded-lg placeholder:text-zinc-500 focus:border-emerald-500/50 focus:ring-0 font-medium text-sm transition-colors";
  const btnBack = "flex-1 h-12 rounded-lg border border-zinc-200 dark:border-white/10 text-zinc-500 font-medium text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all duration-200 ease-out hover:-translate-y-[1px]";
  const btnNext = "flex-[2] h-12 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm transition-all duration-200 ease-out hover:-translate-y-[1px] active:scale-[0.985]";

  const renderAuthPanel = () => (
    <AnimatePresence mode="wait">
      {tab === 'login' && (
        <motion.form key="login" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.35, ease }} onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.3em]">Email</Label>
            <div className="relative group">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-emerald-500 transition-colors" />
              <Input type="email" placeholder="office@yourfirm.com" value={email} onChange={e => setEmail(e.target.value)} required className={cn("pl-12", inputCls)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.3em]">Passphrase</Label>
            <div className="relative group">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-emerald-500 transition-colors" />
              <Input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required className={cn("pl-12", inputCls)} />
            </div>
          </div>
          <button type="submit" disabled={loginLoading}
            className="w-full h-12 rounded-lg bg-zinc-900 dark:bg-white hover:bg-zinc-800 dark:hover:bg-zinc-100 text-white dark:text-zinc-900 font-semibold text-sm transition-all duration-200 ease-out hover:-translate-y-[1px] active:scale-[0.985] disabled:opacity-60 flex items-center justify-center gap-2">
            {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><span>Enter Dashboard</span><ArrowRight className="h-4 w-4" /></>}
          </button>
          <div className="flex justify-between items-center pt-2">
            <button type="button" onClick={() => { setTab('signup'); setRegStep('plans'); }}
              className="text-[11px] font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors">Create account</button>
            <button type="button" onClick={() => { setTab('forgot'); setResetSent(false); setResetEmail(''); }}
              className="text-[11px] font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors">Forgot password?</button>
          </div>
        </motion.form>
      )}

      {tab === 'forgot' && (
        <motion.div key="forgot" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.35, ease }}>
          {resetSent ? (
            <div className="flex flex-col items-center gap-6 py-4 text-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald-500/10 rounded-full blur-2xl animate-pulse" />
                <div className="relative h-16 w-16 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center">
                  <Mail className="h-7 w-7 text-emerald-400" />
                </div>
              </div>
              <div>
                <p className="text-slate-900 dark:text-foreground font-semibold uppercase tracking-widest text-sm mb-2">Check Your Inbox</p>
                <p className="text-muted-foreground text-[11px] font-semibold leading-relaxed">
                  Reset link sent to <span className="text-emerald-400 font-semibold">{resetEmail}</span>
                </p>
              </div>
              <button onClick={() => { setTab('login'); setResetSent(false); }}
                className="text-[9px] font-semibold text-slate-600 hover:text-emerald-400 uppercase tracking-widest transition-colors">← Back to Login</button>
            </div>
          ) : (
            <form onSubmit={handleReset} className="space-y-5">
              <p className="text-muted-foreground text-[11px] font-semibold leading-relaxed">Enter your email and we'll send a secure reset link.</p>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-emerald-500 transition-colors" />
                <Input type="email" placeholder="you@company.com" value={resetEmail} onChange={e => setResetEmail(e.target.value)} required className={cn("pl-12", inputCls)} />
              </div>
              <button type="submit" disabled={resetLoading}
                className="w-full h-13 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-slate-900 dark:text-white font-semibold uppercase tracking-[0.2em] text-[10px] transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2">
                {resetLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Reset Link'}
              </button>
              <div className="flex justify-center">
                <button type="button" onClick={() => setTab('login')}
                  className="text-[9px] font-semibold text-slate-600 hover:text-emerald-400 uppercase tracking-widest transition-colors">← Back to Login</button>
              </div>
            </form>
          )}
        </motion.div>
      )}

      {tab === 'signup' && (
        <AnimatePresence mode="wait">
          {regStep === 'plans' && (
            <motion.div key="plans" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.35, ease }} className="space-y-3">
              {plans.map(plan => (
                <button key={plan.id} onClick={() => setSelectedPlan(plan.id)}
                  className={cn('w-full p-4 rounded-2xl border transition-all relative overflow-hidden group',
                    selectedPlan === plan.id ? 'border-emerald-500 bg-emerald-500/8' : 'border-slate-200 dark:border-border bg-slate-900/5 dark:bg-muted/50 hover:border-slate-200 dark:border-white/')}>
                  <div className="flex items-center gap-4">
                    <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center transition-all',
                      selectedPlan === plan.id ? 'bg-emerald-600 text-black' : 'bg-slate-900/5 dark:bg-muted/50 text-slate-500')}>
                      <plan.icon className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-900 dark:text-foreground font-semibold text-sm uppercase tracking-tight">{plan.name}</span>
                          {plan.badge && (
                            <span className="px-1.5 py-0.5 bg-emerald-500 text-black text-[6px] font-semibold uppercase tracking-wider rounded-md">
                              {plan.badge}
                            </span>
                          )}
                        </div>
                        <span className="text-emerald-400 font-semibold text-[9px] uppercase tracking-wider">{plan.price}</span>
                      </div>
                      <p className="text-slate-600 text-[9px] font-semibold uppercase tracking-widest mt-0.5">{plan.desc}</p>
                    </div>
                    {selectedPlan === plan.id && (
                      <div className="h-6 w-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                        <Check className="h-3.5 w-3.5 text-black" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                </button>
              ))}
              <button onClick={() => setRegStep('identity')}
                className="w-full h-16 rounded-2xl bg-white text-black font-semibold uppercase tracking-[0.25em] text-[11px] shadow-2xl shadow-white/5 transition-all hover:bg-slate-100 hover:scale-[1.02] active:scale-[0.98] mt-4 mb-2">
                Continue with {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}
              </button>
              <div className="flex justify-center pt-1">
                <button onClick={() => setTab('login')} className="text-[9px] font-semibold text-slate-700 hover:text-muted-foreground uppercase tracking-widest transition-colors">Already have an account?</button>
              </div>
            </motion.div>
          )}

          {regStep === 'identity' && (
            <motion.div key="identity" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.35, ease }} className="space-y-5">
              <div className="text-center">
                <p className="text-slate-900 dark:text-foreground font-semibold text-base uppercase tracking-widest mb-1">Who are you?</p>
                <p className="text-slate-600 text-[9px] font-semibold uppercase tracking-widest">Personalises your workspace</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {([{ value: 'company' as const, label: 'Company / Firm', sub: 'Team workspace', icon: '🏢' }, { value: 'individual' as const, label: 'Freelancer', sub: 'Solo workspace', icon: '👤' }] as const).map(opt => (
                  <button key={opt.value} onClick={() => setRegForm(f => ({ ...f, accountType: opt.value }))}
                    className={cn('p-5 rounded-2xl border transition-all text-left space-y-3', regForm.accountType === opt.value ? 'border-emerald-500 bg-emerald-500/8' : 'border-slate-200 dark:border-border bg-slate-900/5 dark:bg-muted/50 hover:border-slate-200 dark:border-white/')}>
                    <span className="text-2xl block">{opt.icon}</span>
                    <div>
                      <p className="text-slate-900 dark:text-foreground font-semibold text-xs uppercase tracking-tight">{opt.label}</p>
                      <p className="text-slate-600 text-[9px] font-semibold uppercase tracking-widest mt-0.5">{opt.sub}</p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setRegStep('plans')} className={btnBack}>← Back</button>
                <button onClick={() => setRegStep('industry')} className={btnNext}>Continue →</button>
              </div>
            </motion.div>
          )}

          {regStep === 'industry' && (
            <motion.div key="industry" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.35, ease }} className="space-y-4">
              <div className="text-center">
                <p className="text-slate-900 dark:text-foreground font-semibold text-base uppercase tracking-widest mb-1">Your Industry</p>
                <p className="text-slate-600 text-[9px] font-semibold uppercase tracking-widest">Sets up the right catalogue</p>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto scrollbar-thin scrollbar-thumb-white/5 pr-1">
                {industries.map(ind => (
                  <button key={ind.slug} onClick={() => setRegForm(f => ({ ...f, industrySlug: ind.slug }))}
                    className={cn('flex flex-col items-start gap-1.5 p-3.5 rounded-xl border transition-all text-left',
                      regForm.industrySlug === ind.slug ? 'border-emerald-500 bg-emerald-500/8' : 'border-slate-200 dark:border-border bg-slate-900/5 dark:bg-muted/50 hover:border-slate-200 dark:border-white/')}>
                    <span className="text-slate-900 dark:text-foreground font-medium text-[10px] uppercase tracking-tight leading-tight">{ind.label}</span>
                    <span className="text-slate-600 text-[8px] font-semibold uppercase tracking-wider leading-snug">{ind.description}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setRegStep('identity')} className={btnBack}>← Back</button>
                <button onClick={() => setRegStep('form')} className={btnNext}>Continue →</button>
              </div>
            </motion.div>
          )}

          {regStep === 'form' && (
            <motion.form key="form" onSubmit={handleSignup} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.35, ease }} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.3em]">{regForm.accountType === 'individual' ? 'Professional Name' : 'Company Name'}</Label>
                <Input placeholder={regForm.accountType === 'individual' ? 'Jane Doe Design' : 'ACME Interiors Ltd.'} value={regForm.companyName} onChange={e => setRegForm(f => ({ ...f, companyName: e.target.value }))} required className={cn("h-12 rounded-xl text-xs", inputCls)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.3em]">Handle</Label>
                  <Input placeholder="dcodeinteriors" value={regForm.nickname} onChange={e => setRegForm(f => ({ ...f, nickname: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') }))} required className={cn("h-12 rounded-xl text-xs", inputCls)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.3em]">Your Name</Label>
                  <Input placeholder="John Doe" value={regForm.fullName} onChange={e => setRegForm(f => ({ ...f, fullName: e.target.value }))} required className={cn("h-12 rounded-xl text-xs", inputCls)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.3em]">Work Email</Label>
                <Input type="email" placeholder="office@mail.com" value={regForm.email} onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))} required className={cn("h-12 rounded-xl text-xs", inputCls)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.3em]">Password</Label>
                <Input type="password" placeholder="Min. 8 chars, 1 uppercase, 1 number" value={regForm.password} onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))} required minLength={8} className={cn("h-12 rounded-xl text-xs", inputCls)} />
                {regForm.password.length > 0 && regForm.password.length < 8 && (
                  <p className="text-red-400 text-[9px] font-semibold uppercase tracking-wider">Minimum 8 characters</p>
                )}
                <p className="text-muted-foreground text-[8px] font-medium uppercase tracking-wider mt-1">Requires 8+ chars, 1 uppercase, 1 lowercase, 1 number</p>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setRegStep('industry')} className={btnBack}>← Back</button>
                <button type="submit" disabled={signing} className={cn(btnNext, "disabled:opacity-60 flex items-center justify-center gap-2")}>
                  {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Workspace'}
                </button>
              </div>
            </motion.form>
          )}

          {regStep === 'sent' && (
            <motion.div key="sent" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, ease }}
              className="flex flex-col items-center gap-6 py-4 text-center">
              <div className="relative">
                <div className="absolute -inset-6 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
                <div className="relative h-20 w-20 bg-emerald-500/10 border border-emerald-500/25 rounded-3xl flex items-center justify-center">
                  <Mail className="h-9 w-9 text-emerald-400" strokeWidth={1.5} />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-semibold text-slate-900 dark:text-foreground tracking-tight uppercase">Check Inbox</h3>
                <p className="text-muted-foreground text-xs font-semibold">Confirmation sent to</p>
                <p className="text-emerald-400 font-semibold text-sm">{regForm.email}</p>
              </div>
              <div className="w-full p-4 rounded-2xl bg-slate-900/5 dark:bg-muted/50 border border-slate-200 dark:border-border space-y-3">
                {['Open the email from DCode DEW', 'Click the confirmation link', 'Your workspace initializes automatically'].map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="h-5 w-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-[9px] font-semibold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider leading-relaxed">{s}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => { setTab('login'); setRegStep('plans'); }}
                className="text-[9px] font-semibold text-slate-600 hover:text-emerald-400 uppercase tracking-widest transition-colors">← Back to Login</button>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </AnimatePresence>
  );

  return (
    <main ref={containerRef} className="relative min-h-screen bg-background overflow-x-hidden font-sans selection:bg-emerald-500/30">

      {/* NAV */}
      <motion.header initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, ease }}
        className="fixed top-0 left-0 right-0 z-[100] h-16 flex items-center px-6 md:px-10 bg-white dark:bg-[#0A0A0A] border-b border-zinc-200 dark:border-white/5">
        <div className="max-w-[1400px] mx-auto w-full flex items-center justify-between">
          <button aria-label="Scroll to top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3 group">
            <div className="h-8 w-8 bg-zinc-900 dark:bg-zinc-100 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-200 ease-out">
              <DPLIcon size={18} className="text-white dark:text-black" />
            </div>
            <div className="hidden sm:block leading-none text-left">
              <p className="text-zinc-900 dark:text-zinc-100 font-semibold text-sm tracking-tight">DCode DEW</p>
            </div>
          </button>
          <nav className="hidden xl:flex items-center gap-8">
            {['Platform', 'Features', 'Workflow', 'Portal'].map(item => (
              <button key={item} onClick={() => document.getElementById(item.toLowerCase())?.scrollIntoView({ behavior: 'smooth' })}
                className="text-[11px] font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 uppercase tracking-[0.12em] transition-colors">
                {item}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <button aria-label="Toggle Theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="h-8 w-8 rounded-lg border border-white/5 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-all flex items-center justify-center">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button onClick={() => document.getElementById('portal')?.scrollIntoView({ behavior: 'smooth' })}
              className="hidden sm:flex items-center gap-2 h-8 px-4 bg-zinc-900 hover:bg-zinc-800 dark:bg-zinc-100 dark:hover:bg-zinc-200 text-white dark:text-zinc-900 font-medium text-[11px] uppercase tracking-wider rounded-lg transition-all duration-200 ease-out active:scale-[0.985]">
              Access Dashboard
            </button>
          </div>
        </div>
      </motion.header>

      {/* HERO */}
      <section id="platform" className="relative min-h-screen flex items-center pt-16 px-6 overflow-hidden" onMouseMove={handleMouseMove}>
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute inset-0 transition-opacity duration-300 ease-out opacity-0 lg:opacity-100"
            style={{
              background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, rgba(255,255,255,0.06), transparent 40%)`
            }}
          />
        </div>
        
        <div className="relative z-10 w-full max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center py-20">
          {/* Left copy */}
          <motion.div variants={stagger} initial="initial" animate="animate" className="flex flex-col space-y-8">
            <motion.div variants={fadeUp} className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-emerald-400 uppercase tracking-[0.12em]">Modern Estimation Infrastructure</span>
            </motion.div>
            
            <motion.h1 variants={fadeUp} className="text-5xl md:text-7xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight leading-[0.95]">
              Quotations that move<br />as fast as your projects.
            </motion.h1>
            
            <motion.p variants={fadeUp} className="text-zinc-500 dark:text-zinc-400 text-lg leading-relaxed max-w-lg">
              Built for interiors, construction teams,<br />and modern project workflows.
            </motion.p>
            
            <motion.div variants={fadeUp} className="flex items-center gap-4 pt-4">
              <button onClick={() => document.getElementById('portal')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm rounded-lg transition-all duration-200 ease-out hover:-translate-y-[1px] active:scale-[0.985]">
                Get Started
              </button>
              <button className="flex items-center justify-center h-12 px-6 border border-zinc-200 dark:border-white/10 bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-semibold text-sm rounded-lg transition-all duration-200 ease-out hover:-translate-y-[1px] active:scale-[0.985]">
                Watch Demo
              </button>
            </motion.div>
          </motion.div>

          {/* Right: Auth card */}
          <div id="portal" className="flex justify-center scroll-mt-28">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.1 }} style={{ y: heroY }} className="w-full max-w-[440px]">
              <div className="relative rounded-2xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-xl shadow-black/5 dark:shadow-black/40 overflow-hidden transition-all duration-200">
                {/* Card header */}
                <div className="px-8 pt-8 pb-6 border-b border-zinc-100 dark:border-white/5">
                  <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    {tab === 'login' ? 'Workspace Access' : tab === 'forgot' ? 'Password Reset' : 'Get Started'}
                  </h2>
                  <p className="text-zinc-500 text-sm mt-1">
                    {tab === 'login' ? 'Sign in to your dashboard' : tab === 'forgot' ? 'Recover your account' : 'Create your workspace'}
                  </p>
                </div>
                {/* Tab switcher */}
                {tab !== 'forgot' && (
                  <div className="px-8 pt-6">
                    <div className="flex p-1 bg-zinc-100 dark:bg-zinc-900 rounded-lg">
                      {(['login', 'signup'] as const).map(t => (
                        <button key={t} onClick={() => { setTab(t); setRegStep('plans'); }}
                          className={cn('flex-1 py-2 text-[11px] font-medium uppercase tracking-[0.12em] rounded-md transition-all duration-200',
                            tab === t ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 shadow-sm' : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300')}>
                          {t === 'login' ? 'Log In' : 'Create Account'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Form */}
                <div className="p-8 pt-6">{renderAuthPanel()}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Ticker />

      {/* STATS */}
      <section className="w-full bg-white dark:bg-[#0A0A0A] border-y border-zinc-200 dark:border-white/5">
        <motion.div variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }}
          className="max-w-[1400px] mx-auto grid grid-cols-2 lg:grid-cols-4 divide-x divide-zinc-200 dark:divide-white/5">
          {[{ value: '99.9%', label: 'Calculation Accuracy', sub: 'Financial precision' }, { value: '85%', label: 'Time Saved', sub: 'Vs manual quoting' }, { value: '40%', label: 'Conversion Lift', sub: 'Deal closure rate' }, { value: '99.99%', label: 'Uptime', sub: 'Cloud reliability' }].map(s => (
            <motion.div key={s.label} variants={fadeUp} className="py-14 px-8 flex flex-col items-center text-center transition-colors">
              <span className="text-4xl xl:text-5xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight mb-3">{s.value}</span>
              <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-[0.12em] mb-1">{s.label}</span>
              <span className="text-[10px] font-medium text-zinc-400">{s.sub}</span>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* FEATURES */}
      <section id="features" className="w-full py-32 px-6 relative overflow-hidden scroll-mt-20">
        <div className="max-w-[1400px] mx-auto relative z-10">
          <motion.div variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }} className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16">
            <motion.div variants={fadeUp} className="space-y-6">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-zinc-500" />
                <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-[0.12em]">Core Capabilities</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight leading-[1.05]">Engineered for<br />Estimation</h2>
              <p className="text-zinc-500 text-lg leading-relaxed max-w-xl">DEW eliminates the friction between spatial design and commercial reality. Fast, precise, and unforgettably branded.</p>
            </motion.div>
            <motion.div variants={fadeUp}>
              <button className="h-12 px-8 rounded-lg border border-zinc-200 dark:border-white/10 bg-transparent text-zinc-900 dark:text-zinc-100 font-medium text-sm transition-all duration-200 ease-out hover:bg-zinc-100 dark:hover:bg-zinc-900 hover:-translate-y-[1px] active:scale-[0.985]">View Specifications</button>
            </motion.div>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <BentoCard icon={FileText} title="Proposals" sub="Client Experience" desc="Cinematic PDF proposals with branded letterheads, full room breakdowns, and integrated payment schedules." accent wide delay={0} />
            <BentoCard icon={Layers} title="Catalogue" sub="Item Intelligence" desc="Centralized multi-industry catalogue. Map materials and labour costs with AI-enriched pricing data." delay={0.1} />
            <BentoCard icon={TrendingUp} title="Analytics" sub="Business Intel" desc="Pipeline health, conversion rates, and profit margin tracking — all in a mission-critical dashboard." delay={0.2} />
            <BentoCard icon={BookOpen} title="Accounting" sub="Financial Control" desc="Keep cashflow, ledgers, GST and cost center tracking aligned with estimates, invoices and project budgets." accent delay={0.18} />
            <BentoCard icon={Zap} title="Invoices" sub="Financial Flow" desc="Sequential invoice generation linked to quotation revisions. Real-time payment status tracking." delay={0.15} />
            <BentoCard icon={Sparkles} title="RAVAN AI" sub="AI Co-pilot" desc="Retrieval-Augmented Virtual AI Navigator — your intelligent estimation assistant, built right in." accent delay={0.25} />
            <BentoCard icon={Layout} title="PWA First" sub="Field Ready" desc="Installable on all devices. Access estimates and client data anywhere, even offline." delay={0.3} />
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section id="workflow" className="w-full py-32 px-6 bg-white dark:bg-[#0A0A0A] border-y border-zinc-200 dark:border-white/5 scroll-mt-20">
        <div className="max-w-[1400px] mx-auto">
          <motion.div variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }} className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-24">
            <motion.div variants={fadeUp} className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight leading-[1.1]">Optimised<br />Workflow</h2>
                <p className="text-zinc-500 text-lg leading-relaxed mt-6">A streamlined three-step sequence — lead to signed contract with zero document friction.</p>
              </div>
              <div className="space-y-4 pt-6 border-t border-zinc-200 dark:border-white/5">
                {[{ n: '01', label: 'Catalogue Selection', active: true }, { n: '02', label: 'Automated Calculation', active: false }, { n: '03', label: 'Proposal Generation', active: false }].map(s => (
                  <div key={s.n} className="flex items-center gap-4 group">
                    <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center font-medium text-sm transition-all duration-200 ease-out',
                      s.active ? 'bg-emerald-500 text-black' : 'bg-zinc-100 dark:bg-zinc-900 text-zinc-500 group-hover:bg-zinc-200 dark:group-hover:bg-zinc-800 group-hover:text-zinc-900 dark:group-hover:text-zinc-100')}>{s.n}</div>
                    <span className="text-zinc-900 dark:text-zinc-100 font-medium text-sm">{s.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div variants={stagger} className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[{ icon: Layout, title: 'Room Configurator', desc: 'Deep multi-room nesting with custom scope definitions and unit-rate editing.' }, { icon: Cpu, title: 'Bulk Processing', desc: 'Edit margins, GST, and discounts across entire projects in one transaction.' }, { icon: Clock, title: 'Revision History', desc: 'Lossless tracking of all estimate versions. Restore any state in one click.' }, { icon: Compass, title: 'Multi-Industry', desc: 'Tailored templates for Interior Design, Civil, Landscape, and more.' }, { icon: Printer, title: 'Print Engine', desc: 'High-fidelity print modules for proposals, BOQ, and tax invoices.' }, { icon: Shield, title: 'RBAC Security', desc: 'Role-based access control ensuring data integrity across your entire team.' }].map(item => (
                <motion.div key={item.title} variants={fadeUp} className="flex gap-4 p-6 rounded-2xl bg-zinc-50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-all duration-200 ease-out hover:-translate-y-[1px] group">
                  <div className="text-zinc-400 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-zinc-900 dark:text-zinc-100 font-semibold text-sm mb-1.5">{item.title}</h4>
                    <p className="text-zinc-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="w-full pt-24 pb-12 px-6 bg-white dark:bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-2 space-y-6">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-zinc-900 dark:bg-zinc-100 rounded-lg flex items-center justify-center"><DPLIcon size={18} className="text-white dark:text-black" /></div>
                <div>
                  <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">DCode DEW</h2>
                </div>
              </div>
              <p className="text-zinc-500 text-sm leading-relaxed max-w-xs">Professional interior design estimation and project financial management. The digital backbone for modern space engineering firms.</p>
              <div className="flex items-center gap-3">
                <button aria-label="Contact Support on WhatsApp" onClick={() => window.open('https://wa.me/919645760001', '_blank')} className="h-10 w-10 rounded-lg border border-zinc-200 dark:border-white/5 flex items-center justify-center text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-all"><MessageCircle className="h-4 w-4" /></button>
                <button aria-label="Visit Website" onClick={() => window.open('https://www.dew.dcodeinteriors.com', '_blank')} className="h-10 w-10 rounded-lg border border-zinc-200 dark:border-white/5 flex items-center justify-center text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-all"><Zap className="h-4 w-4" /></button>
              </div>
            </div>
            <div>
              <h4 className="text-zinc-400 font-medium text-[11px] uppercase tracking-[0.12em] mb-6">Platform</h4>
              <ul className="space-y-3">{['Features', 'Workflow', 'Pricing', 'Security'].map(l => <li key={l}><a href="#" className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 text-sm transition-colors">{l}</a></li>)}</ul>
            </div>
            <div>
              <h4 className="text-zinc-400 font-medium text-[11px] uppercase tracking-[0.12em] mb-6">Legal</h4>
              <ul className="space-y-3">{[{ label: 'Privacy', href: '/legal/privacy' }, { label: 'Terms', href: '/legal/privacy' }, { label: 'Security', href: '/legal/security' }, { label: 'SLA', href: '/legal/privacy' }].map(l => <li key={l.label}><a href={l.href} className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 text-sm transition-colors">{l.label}</a></li>)}</ul>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-200 dark:border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-zinc-500 text-sm">© 2026 DCode DEW — DCODE Private Limited.</p>
            <button onClick={() => window.open('https://www.dew.dcodeinteriors.com', '_blank')} className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 text-sm transition-colors">Visit DCODE Corporate →</button>
          </div>
        </div>
      </footer>
    </main>
  );
}
